import { ColumnState, IAggFunc } from '@ag-grid-community/core';
import { TimePrecision } from '@blueprintjs/datetime';
import { ITipLabel } from 'welltrax-ui-components/lib/types/Form/Inputs/CustomDateRange/type/ICustomDateTimePicker';
import { LayoutType } from '../../modules/commons/types/LayoutTypes';
import { IUser } from '../interface/User';
import { ICellRender } from './CellRenderType';
import { IItem } from './IItem';

export type IViewTypes =
  | 'OPERATOR'
  | 'TERMINAL'
  | 'DRIVER'
  | 'TOTALS'
  | 'SCHEDULE_MASTER'
  | 'All'
  | 'PICKUP'
  | 'DROPOFF'
  | 'CODE'
  | 'DRIVERSCHEDULE'
  | 'TRUCKTRAILER'
  | 'CONTRACTOR'
  | 'ACCOUNT'
  | 'STATION_LOG_DRIVER'
  | 'COMMODITY'
  | 'REASONCODE'
  | 'MANAGE_LOAD'
  | 'MANAGE_SCENARIO'
  | 'SECONDARY_SCENARIO'
  | 'DISPATCH_DRIVER'
  | 'DISPATCH_LOAD'
  | 'DISPATCH_DRIVER_DETAILS'
  | 'DISPATCH_INVOICE'
  | 'DISPATCH_PAYROLL'
  | 'DISPATCH_DRIVER_WORKING_TODAY'
  | 'REVIEW_RECONCILE_LOAD'
  | 'REVIEW_DVIR'
  | 'REVIEW_DRIVER_ADDED_TANKS'
  | 'FUEL_PURCHASE'
  | 'VEHICLE_MAINTENANCE'
  | 'REVIEW_RTU'
  | 'REVIEW_STATION_LOGS_TICKETS'
  | 'REVIEW_STATION_LOGS_OPEN_CLOSE'
  | 'MASTER_INVOICE'
  | 'INVOICE_TOTALS'
  | 'DISPATCH_DRIVER_SHIFT_EMAIL'
  | 'INVOICE_TICKETS_DETAILS'
  | 'FINANCE_RATE_SHEET'
  | 'FINANCE_CONTRACT'
  | 'MASTER_PAYROLL'
  | 'PAYROLL_TICKETS_DETAILS'
  | 'PAYROLL_SETTLEMENTS'
  | 'CONTRACT'
  | 'MANAGE_SCHEDULE_LOAD'
  | 'MANAGE_LOAD_FORM'
  | 'SETUP_CONTRACT';

export type FilterOperator =
  | 'EQUALITY'
  | 'NEGATION'
  | 'GREATER_THAN'
  | 'LESS_THAN'
  | 'LIKE'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'CONTAINS'
  | 'NUMBER_LESS_EQUAL_THAN'
  | 'NUMBER_GREATER_EQUAL_THAN'
  | 'COMPARABLE_LESS_EQUAL_THAN'
  | 'COMPARABLE_GREATER_EQUAL_THAN'
  | 'GREATER_EQUALS_THAN'
  | 'LESS_EQUALS_THAN'
  | 'DATE_GREATER_EQUAL_THAN'
  | 'DATE_LESS_EQUAL_THAN'
  | 'DATE_GREATER_THAN'
  | 'DATE_LESS_THAN'
  | 'DATE_EQUAL'
  | 'NOT_IN'
  | 'IN';

export interface IFilterType {
  field: string;
  value: any;
  operator: FilterOperator;
  active?: boolean;
  type?: IFilterInputType;
  source?: any; // see value types in IColumns Source;
  typeSource?: IColumnsSource;
  urlType?: IUrlFilterTyle;
  replaceSource?: string[];
  source_value?: string; // value to fill the select
  options?: any[];
  label?: string;
  order?: number;
  additional_value?: any;
  fieldKeys?: string[];
  hidden?: boolean;
  isAdditional?: boolean;
  width?: string;
  // For Select Type
  notAllowEmptyItem?: boolean;
  // For Boolean Type
  excludeBoth?: boolean;
  booleanLabels?: { [key: string]: string };
  disabledOperator?: boolean;
  timePrecision?: TimePrecision;
  useAmPm?: boolean;
  canClearSelection?: boolean;
  filters?: IFilterType[];
  disabledContext?: string;
  sourceToSaveInContext?: string;
  shortcuts?: EShortcuts[];
  calculateValue?: any;
  replaceSourceQueryParams?: ISourceQueryParams;
  labelConditional?: { [key: string]: string };
  conditionalInContext?: string;
  saveInContext?: string;
  calculateMaxTime?: any;
  disabledSort?: boolean;
  keyStatusLabel?: string;
  section?: string;
  dateType?: EShortcuts | '';
  subLabels?: ITipLabel;
  additionalSourceDescriptionInOptions?: ISourceDescriptionOptions[];
  displayAsTree?: boolean;
  treeChildIndentWidth?: number;
  tagValidation?: any;
  isSpecific?: boolean;
  property?: string;
}

export interface IShortcuts {
  type: EShortcuts;
  label: string;
  dateRange: any[];
}

export interface ISourceQueryParams {
  from: string;
  to?: string;
}

export enum EShortcuts {
  YESTERDAY = 'YESTERDAY',
  YESTERDAY_7_AM_TO_7_AM = 'YESTERDAY_7_AM_TO_7_AM',
  TODAY = 'TODAY',
  TODAY_7_AM_TO_7_AM = 'TODAY_7_AM_TO_7_AM',
  TOMORROW = 'TOMORROW',
  THIS_WEEK = 'THIS_WEEK',
  WEEK_TO_DATE = 'WEEK_TO_DATE',
  LAST_WEEK = 'LAST_WEEK',
  THIS_MONTH = 'THIS_MONTH',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  LAST_MONTH = 'LAST_MONTH',
  THIS_YEAR = 'THIS_YEAR',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
  USER_DEFINED = 'USER_DEFINED'
}

export interface ISourceDescriptionOptions {
  source: string;
  startPath?: string;
  endPath?: string;
}

export interface IConditionalsType extends IFilterType {
  format: any;
}

export interface ILayoutType {
  field: string;
  label: string;
  type: IFilterInputType;
  value: any;
  options?: IItem[];
  booleanLabels?: { [key: string]: string };
  margin?: number;
}

export type IUrlFilterTyle = 'REPLACE' | 'CONCAT' | 'MULTIPLE_REPLACE';
export type ITotalsTypes = 'Average' | 'Sum' | 'None';

export type IFilterInputType =
  | 'NUMBER'
  | 'BOOLEAN'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'INPUT'
  | 'TEXTAREA'
  | 'DATETIME'
  | 'DATE'
  | 'TIME'
  | 'DATERANGE'
  | 'PERCENT'
  | 'COLOR'
  | 'MULTI_SELECT_TAGS'
  | 'ADVANCE_SECTION'
  | 'ADVANCE_HIDE_SECTION'
  | 'INPUT_TAG'
  | 'GROUP_BUTTONS'
  | 'CUSTOM_DATE_RANGE'
  | 'AUTOCOMPLETE'
  | 'CUSTOM_TIME_SELECT'
  | 'BOOLEAN-RADIO';

export type IColumnsSource = 'URL' | 'ARRAY' | 'ANY' | 'ARRAY_CONTEXT' | 'ARRAY_CONTEXT_OBJECT';

export type ISourceType =
  | 'OBJECT'
  | 'CONCAT_OBJECT'
  | 'STRING_CONCAT_OBJECT'
  | 'URL_ARRAY_CONCAT_OBJECT'
  | 'ARRAY_CONCAT'
  | 'ARRAY_ZERO_CONCAT_OBJECT'
  | 'ARRAY_ZERO_ARRAY_CONCAT_OBJECT'
  | 'ARRAY_CONCAT_OBJECT'
  | 'ARRAY_ZERO_STRING_CONCAT_OBJECT'
  | 'ARRAY_ZERO_URL_ARRAY_CONCAT_OBJECT'
  | 'ARRAY_ZERO_ARRAY_CONCAT'
  | 'RESOURCE_OBJECT'
  | 'ARRAY_ZERO_RESOURCE_OBJECT'
  | 'CONCAT_OBJECT_ARRAY_ZERO_ARRAY_CONCAT_OBJECT'
  | 'CONDITIONAL_CONCAT_OBJECT'
  | 'ARRAY_CONCAT_OBJECT_CONTACT_STRING'
  | 'ARRAY_CONCAT_OBJECT_ACTIVE'
  | 'CONCAT_DATE_TIME_OBJECT';

export interface IColumnResourceType {
  resource: string;
  property: string;
}

export type IColumnType = 'STRING' | 'DATE' | 'NUMBER' | 'BOOLEAN' | 'COLOR' | 'DATE_TIME';

export interface IReplaceInValue {
  from: string;
  to: string;
}

export interface IColumnsRep {
  name: string;
  order?: number;
  visible: boolean;
  label: string;
  totals?: ITotalsTypes;
  type?: IColumnType;
  defaultName?: string;
  sort?: string;
  sortIndex?: number;
  disableSort?: boolean;
  width?: number;
  sourceType?: ISourceType;
  url?: string;
  concatSym?: string;
  concatMainSym?: string;
  source?: string;
  sourceValidation?: string;
  cellRenderer?: ICellRender;
  calculable?: boolean;
  resource?: IColumnResourceType;
  defaultValue?: any;
  labelHasUpdate?: boolean;
  isAdditional?: boolean;
  commodity?: number;
  layoutType?: LayoutType;
  subColumns?: IColumnsRep[];
  chartDataType?: ChartColumnType;
  aggFunc?: string | IAggFunc | null;
  pivotIndex?: number | null;
  rowGroupIndex?: number | null;
  replaceInValue?: IReplaceInValue;
  template?: string;
  dynamicFieldId?: number | string;
  dependencyFormId?: number | string;
  dynamicFieldType?: string | null;
  extraFormat?: ExtraFormat | null;
}

export interface ExtraFormat {
  isThousandSeparator?: boolean | null;
  format?: string | null;
  numberOfDecimals?: number | null;
}

export type ProfileLevelType = 'USER' | 'COMPANY' | 'USER_GROUP';

export interface IProfileType {
  id: string | number;
  type: IViewTypes;
  name: string;
  user_id: string;
  created_by?: string;
  is_default: boolean;
  is_global: boolean;
  is_read_only: boolean;
  filters: IFilterType[];
  conditionals: IConditionalsType[];
  columns: IColumnsRep[];
  pivot: ColumnState[];
  details_locked: boolean;
  details_collapse: boolean;
  layout?: ILayoutType[];
  queryFilters?: IFilterType[];
  mode?: ProfileModes;
  profileLevel: ProfileLevelType;
  userGroupsIds: number[];
  editUsersIds: number[];
  viewUsersIds: number[];
  isActive: boolean;
}

export interface IMasterProfileType {
  id: string | number;
  type: IViewTypes;
  name: string;
  user_id: string;
  created_by?: string;
  is_read_only: boolean;
  is_default: boolean;
  is_global: boolean;
  profiles: IProfileType[];
  details_locked?: boolean;
  details_collapse?: boolean;
  queryFilters?: IFilterType[];
  profileLevel: ProfileLevelType;
  userGroupsIds: number[];
  editUsersIds: number[];
  viewUsersIds: number[];
  isActive: boolean;
}

export interface IVeltraxProfileType {
  id: string | number;
  type: IViewTypes;
  name: string;
  createdByUser: IUser;
  isGlobal: boolean;
  isDefault: boolean;
  isReadOnly: boolean;
  preference: string;
  profileLevel: ProfileLevelType;
  userGroupsIds: number[];
  editUsersIds: number[];
  viewUsersIds: number[];
  isActive: boolean;
}

export enum ProfileModes {
  'BASIC' = 'BASIC',
  'PIVOT' = 'PIVOT',
  'CHART' = 'CHART'
}

export type ChartColumnType = 'category' | 'series' | 'excluded' | undefined;
